import { z } from "zod";

import {
  retentionDaysSchema,
  type Plan,
  type RetentionDays,
  type RetentionEntitlementDays,
} from "./storage";

const trelloIdentifierSchema = z.string().min(1).max(128);

export const billingStatusSchema = z.enum([
  "not_subscribed",
  "trialing",
  "active",
  "past_due",
  "paused",
  "canceled",
]);
export type BillingStatus = z.infer<typeof billingStatusSchema>;

export const paidPlanVariantSchema = z.enum(["standard", "extended"]);
export type PaidPlanVariant = z.infer<typeof paidPlanVariantSchema>;

export const effectiveCloudPlanSchema = z.enum(["free", "pro", "pro_extended"]);
export type EffectiveCloudPlan = z.infer<typeof effectiveCloudPlanSchema>;

export const cloudScheduleStatusSchema = z.enum([
  "inactive",
  "active",
  "action_required",
]);
export type CloudScheduleStatus = z.infer<typeof cloudScheduleStatusSchema>;

const localTimeSchema = z
  .string()
  .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, "Use a 24-hour HH:mm time.");
const timeZoneSchema = z.string().min(1).max(128);
const weekdaySchema = z.enum([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);

export const cloudScheduleFrequencySchema = z.enum([
  "off",
  "six_hourly",
  "twelve_hourly",
  "daily",
  "weekly",
]);
export type CloudScheduleFrequency = z.infer<
  typeof cloudScheduleFrequencySchema
>;

export const cloudScheduleSchema = z.discriminatedUnion("frequency", [
  z.object({ frequency: z.literal("off") }),
  z.object({
    frequency: z.literal("six_hourly"),
    localTime: localTimeSchema,
    timeZone: timeZoneSchema,
  }),
  z.object({
    frequency: z.literal("twelve_hourly"),
    localTime: localTimeSchema,
    timeZone: timeZoneSchema,
  }),
  z.object({
    frequency: z.literal("daily"),
    localTime: localTimeSchema,
    timeZone: timeZoneSchema,
  }),
  z.object({
    frequency: z.literal("weekly"),
    localTime: localTimeSchema,
    timeZone: timeZoneSchema,
    weekday: weekdaySchema,
  }),
]);
export type CloudSchedule = z.infer<typeof cloudScheduleSchema>;

export const cloudEntitlementsSchema = z.object({
  availableScheduleFrequencies: z.array(cloudScheduleFrequencySchema).min(1),
  attachmentCopies: z.boolean(),
  billingAttention: z.boolean(),
  billingStatus: billingStatusSchema,
  compare: z.boolean(),
  effectivePlan: effectiveCloudPlanSchema,
  manualSnapshotPolicy: z.enum(["one_time_workspace_trial", "unlimited"]),
  maxProtectedBoards: z.number().int().positive(),
  restore: z.boolean(),
  retentionDays: z.array(retentionDaysSchema).min(1),
  scheduledSnapshots: z.boolean(),
  storagePackBytes: z.number().int().nonnegative(),
  storagePackCount: z.number().int().nonnegative(),
  workspaceStorageLimitBytes: z.number().int().positive(),
});
export type CloudEntitlements = z.infer<typeof cloudEntitlementsSchema>;

const paidAccessStatuses = new Set<BillingStatus>([
  "trialing",
  "active",
  "past_due",
]);

export function resolveCloudEntitlements(input: {
  billingStatus: BillingStatus;
  maxRetentionDays: RetentionEntitlementDays;
  plan: Plan;
  planVariant?: PaidPlanVariant;
  storagePackCount?: number;
}): CloudEntitlements {
  const paid =
    input.plan === "pro" && paidAccessStatuses.has(input.billingStatus);
  const extended = paid && input.planVariant === "extended";
  const effectivePlan: EffectiveCloudPlan = !paid
    ? "free"
    : extended
      ? "pro_extended"
      : "pro";
  const retentionCeiling = extended
    ? Math.max(30, input.maxRetentionDays)
    : Math.min(7, input.maxRetentionDays);
  const baseChoices: readonly RetentionDays[] = extended
    ? [1, 3, 7, 14, 21, 30]
    : paid
      ? [1, 3, 7]
      : [7];
  const retentionDays = baseChoices.filter((days) => days <= retentionCeiling);
  const storagePackCount = Math.max(0, input.storagePackCount ?? 0);
  const baseStorageBytes = extended
    ? 100_000_000_000
    : paid
      ? 25_000_000_000
      : 250_000_000;
  const storagePackBytes = paid ? storagePackCount * 50_000_000_000 : 0;

  return cloudEntitlementsSchema.parse({
    availableScheduleFrequencies: extended
      ? ["off", "six_hourly", "twelve_hourly", "daily", "weekly"]
      : paid
        ? ["off", "daily", "weekly"]
        : ["off"],
    attachmentCopies: true,
    billingAttention: input.billingStatus === "past_due",
    billingStatus: input.billingStatus,
    compare: paid,
    effectivePlan,
    manualSnapshotPolicy: paid ? "unlimited" : "one_time_workspace_trial",
    maxProtectedBoards: paid ? 5 : 1,
    restore: paid,
    retentionDays,
    scheduledSnapshots: paid,
    storagePackBytes,
    storagePackCount,
    workspaceStorageLimitBytes: baseStorageBytes + storagePackBytes,
  });
}

export const cloudBoardSettingsSchema = z.object({
  nextSnapshotAt: z.iso.datetime().nullable(),
  retentionDays: retentionDaysSchema,
  retentionTestMinutes: z.literal(5).nullable().optional(),
  schedule: cloudScheduleSchema,
  scheduleTestMinutes: z.literal(10).nullable().optional(),
  scheduleStatus: cloudScheduleStatusSchema,
});
export type CloudBoardSettings = z.infer<typeof cloudBoardSettingsSchema>;

export const cloudBoardUsageSchema = z.object({
  protectedBoardCount: z.number().int().nonnegative(),
  workspaceStorageBytes: z.number().int().nonnegative(),
});
export type CloudBoardUsage = z.infer<typeof cloudBoardUsageSchema>;

export const cloudTrialStateSchema = z.discriminatedUnion("status", [
  z.object({
    retentionHours: z.literal(48),
    status: z.literal("available"),
  }),
  z.object({
    boardId: trelloIdentifierSchema,
    jobId: z.uuid(),
    reservedAt: z.iso.datetime(),
    retentionHours: z.literal(48),
    snapshotId: z.uuid(),
    status: z.literal("reserved"),
  }),
  z.object({
    boardId: trelloIdentifierSchema,
    consumedAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    retentionHours: z.literal(48),
    snapshotId: z.uuid().nullable(),
    status: z.literal("consumed"),
  }),
]);
export type CloudTrialState = z.infer<typeof cloudTrialStateSchema>;

export const cloudZipRestoreTrialStateSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("available") }),
  z.object({
    consumedAt: z.iso.datetime(),
    jobId: z.uuid().nullable(),
    snapshotId: z.uuid().nullable(),
    status: z.literal("used"),
  }),
]);
export type CloudZipRestoreTrialState = z.infer<
  typeof cloudZipRestoreTrialStateSchema
>;

export const cloudCaptureRequestSchema = z.object({
  idempotencyKey: z.uuid(),
});
export type CloudCaptureRequest = z.infer<typeof cloudCaptureRequestSchema>;

export const cloudCaptureResponseSchema = z.object({
  acceptedAt: z.iso.datetime(),
  jobId: z.uuid(),
  snapshotId: z.uuid(),
  status: z.enum(["accepted", "in_progress"]),
  trial: cloudTrialStateSchema,
});
export type CloudCaptureResponse = z.infer<typeof cloudCaptureResponseSchema>;

export const cloudCaptureStatusSchema = z.object({
  capturePurpose: z.enum(["standard", "restore_safety"]).optional(),
  completedAt: z.iso.datetime().nullable(),
  controlState: z.enum([
    "active",
    "pause_requested",
    "paused",
    "cancel_requested",
    "canceled",
  ]),
  createdAt: z.iso.datetime(),
  errorCode: z.string().min(1).max(128).nullable(),
  jobId: z.uuid(),
  jobStatus: z.enum([
    "pending",
    "running",
    "paused",
    "canceled",
    "complete",
    "complete_with_warnings",
    "failed",
  ]),
  progress: z.object({
    completed: z.int().nonnegative(),
    total: z.int().nonnegative().nullable(),
  }),
  snapshotId: z.uuid(),
  snapshotStatus: z.enum([
    "pending",
    "capturing_structure",
    "capturing_attachments",
    "building_archive",
    "complete",
    "complete_with_warnings",
    "failed",
  ]),
  startedAt: z.iso.datetime().nullable(),
});
export type CloudCaptureStatus = z.infer<typeof cloudCaptureStatusSchema>;

export const cloudActiveCapturesSchema = z.object({
  captures: z.array(cloudCaptureStatusSchema).max(5),
});
export type CloudActiveCaptures = z.infer<typeof cloudActiveCapturesSchema>;

export const jobControlActionSchema = z.object({
  action: z.enum(["pause", "resume", "cancel", "retry"]),
});
export type JobControlAction = z.infer<typeof jobControlActionSchema>;

export const jobControlStateSchema = z.enum([
  "active",
  "pause_requested",
  "paused",
  "cancel_requested",
  "canceled",
]);
export type JobControlState = z.infer<typeof jobControlStateSchema>;

export const jobControlResponseSchema = z.object({
  controlState: jobControlStateSchema,
  jobId: z.uuid(),
  jobType: z.enum(["capture", "restore"]),
  status: z.enum([
    "pending",
    "running",
    "paused",
    "canceled",
    "complete",
    "complete_with_warnings",
    "failed",
  ]),
  updatedAt: z.iso.datetime(),
});
export type JobControlResponse = z.infer<typeof jobControlResponseSchema>;

export const portalHandoffResponseSchema = z.object({
  expiresAt: z.iso.datetime(),
  portalUrl: z.url(),
});
export type PortalHandoffResponse = z.infer<typeof portalHandoffResponseSchema>;

export const cloudSnapshotSummarySchema = z.object({
  attachmentOutcomes: z.object({
    copied: z.int().nonnegative(),
    failed: z.int().nonnegative(),
    linkOnly: z.int().nonnegative(),
    skipped: z.int().nonnegative(),
  }),
  capturedAt: z.iso.datetime(),
  capturePurpose: z.enum(["standard", "restore_safety"]).optional(),
  captureTier: z.enum(["trial", "pro"]),
  expiresAt: z.iso.datetime(),
  id: z.uuid(),
  status: z.enum(["complete", "complete_with_warnings"]),
  totalBytes: z.int().nonnegative(),
  warningCount: z.int().nonnegative(),
});
export type CloudSnapshotSummary = z.infer<typeof cloudSnapshotSummarySchema>;

export const cloudSnapshotHistorySchema = z.object({
  failedCaptures: z.array(cloudCaptureStatusSchema).max(30).default([]),
  scheduleState: z
    .object({
      nextSnapshotAt: z.iso.datetime().nullable(),
      scheduleStatus: cloudScheduleStatusSchema,
    })
    .optional(),
  snapshots: z.array(cloudSnapshotSummarySchema),
});
export type CloudSnapshotHistory = z.infer<typeof cloudSnapshotHistorySchema>;

export const cloudSnapshotDeleteRequestSchema = z.object({
  confirmation: z.literal("Delete"),
});
export type CloudSnapshotDeleteRequest = z.infer<
  typeof cloudSnapshotDeleteRequestSchema
>;

export const cloudSnapshotDeleteResponseSchema = z.object({
  scheduledAt: z.iso.datetime(),
  snapshotId: z.uuid(),
  status: z.literal("scheduled"),
});
export type CloudSnapshotDeleteResponse = z.infer<
  typeof cloudSnapshotDeleteResponseSchema
>;

export const cloudSnapshotWarningSchema = z.object({
  card: z.object({
    id: trelloIdentifierSchema,
    name: z.string().min(1),
  }),
  code: z.string().regex(/^[A-Z][A-Z0-9_]{2,63}$/),
  item: z.object({
    id: trelloIdentifierSchema,
    name: z.string().min(1),
  }),
  message: z.string().min(1).max(500),
  objectType: z.literal("attachment"),
  outcome: z.enum(["failed", "skipped"]),
  sourceLinkPreserved: z.boolean(),
});
export type CloudSnapshotWarning = z.infer<typeof cloudSnapshotWarningSchema>;

export const cloudSnapshotWarningsSchema = z.object({
  snapshotId: z.uuid(),
  warnings: z.array(cloudSnapshotWarningSchema),
});
export type CloudSnapshotWarnings = z.infer<typeof cloudSnapshotWarningsSchema>;

export const cloudSettingsUpdateRequestSchema = z.object({
  retentionDays: retentionDaysSchema,
  retentionTestMinutes: z.literal(5).nullable().optional(),
  schedule: cloudScheduleSchema,
  scheduleTestMinutes: z.literal(10).nullable().optional(),
});
export type CloudSettingsUpdateRequest = z.infer<
  typeof cloudSettingsUpdateRequestSchema
>;

export const cloudBoardStateSchema = z.object({
  accountId: z.uuid(),
  board: z.object({
    id: trelloIdentifierSchema,
    name: z.string().min(1),
  }),
  entitlements: cloudEntitlementsSchema,
  settings: cloudBoardSettingsSchema,
  trial: cloudTrialStateSchema,
  // Optional during the staged API/client rollout. New API responses always
  // include authoritative Workspace usage; older responses remain readable.
  usage: cloudBoardUsageSchema.optional(),
  zipRestoreTrial: cloudZipRestoreTrialStateSchema.optional(),
  workspace: z.object({
    id: trelloIdentifierSchema,
    name: z.string().min(1),
  }),
});
export type CloudBoardState = z.infer<typeof cloudBoardStateSchema>;

export const cloudConnectionRequestSchema = z.object({
  trelloToken: z.string().min(1).max(4096),
});
export type CloudConnectionRequest = z.infer<
  typeof cloudConnectionRequestSchema
>;

export const trelloJwtClaimsSchema = z.object({
  exp: z.number().int().positive(),
  iat: z.number().int().positive(),
  idBoard: trelloIdentifierSchema,
  idMember: trelloIdentifierSchema,
  idPlugin: trelloIdentifierSchema,
  iss: z.literal("trello"),
  state: z.string().max(2048).optional(),
});
export type TrelloJwtClaims = z.infer<typeof trelloJwtClaimsSchema>;

export const cloudConnectionResponseSchema = cloudBoardStateSchema.extend({
  credentialStatus: z.literal("active"),
  protected: z.literal(true),
});
export type CloudConnectionResponse = z.infer<
  typeof cloudConnectionResponseSchema
>;

export const cloudDownloadGrantResponseSchema = z.object({
  downloadUrl: z.url().refine((value) => value.startsWith("https://")),
  expiresAt: z.iso.datetime(),
});
export type CloudDownloadGrantResponse = z.infer<
  typeof cloudDownloadGrantResponseSchema
>;
